import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, MessageSquare, Building2, User, ArrowRight, Send, Globe, Facebook, Twitter, Instagram, Linkedin, ChevronRight, Calendar, FileText, Users, Star, Shield, PenTool as Tool } from 'lucide-react';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';

const Contact: React.FC = () => {
  const location = useLocation();
  const [selectedOffice, setSelectedOffice] = useState('dakar');
  const [contactReason, setContactReason] = useState('');

  useEffect(() => {
    document.title = 'Contact | Proxy Emobility';
    
    // Parse query parameters
    const searchParams = new URLSearchParams(location.search);
    const course = searchParams.get('course');
    const type = searchParams.get('type');
    
    if (course) {
      setContactReason(`formation-${course}`);
    } else if (type === 'trial') {
      setContactReason('essai');
    } else if (type === 'info') {
      setContactReason('information');
    }
  }, [location]);

  const offices = [
    {
      id: 'dakar',
      city: 'Dakar',
      country: 'Sénégal',
      address: '123 Avenue de l\'Indépendance',
      phone: '+221 77 123 45 67',
      email: 'dakar@proxy-emobility.com',
      hours: 'Lun - Ven: 9h - 18h',
      coordinates: [14.6937, -17.4441],
      team: [
        { name: 'Amadou Diallo', role: 'Directeur Régional' },
        { name: 'Fatou Sow', role: 'Responsable Commercial' }
      ],
      image: 'https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg'
    },
    {
      id: 'london',
      city: 'London',
      country: 'England',
      address: '20 Wenlock Road, N1 7GU',
      phone: '+44 20 1234 5678',
      email: 'london@proxy-emobility.com',
      hours: 'Mon - Fri: 9am - 5pm',
      coordinates: [51.5074, -0.1278],
      team: [
        { name: 'James Smith', role: 'European Director' },
        { name: 'Emily Johnson', role: 'Business Development' }
      ],
      image: 'https://images.pexels.com/photos/672532/pexels-photo-672532.jpeg'
    }
  ];

  const stats = [
    {
      icon: <Globe size={24} className="text-primary-600" />,
      value: "10+",
      label: "Pays"
    },
    {
      icon: <Building2 size={24} className="text-primary-600" />,
      value: "2",
      label: "Bureaux"
    },
    {
      icon: <Users size={24} className="text-primary-600" />,
      value: "50+",
      label: "Employés"
    },
    {
      icon: <Star size={24} className="text-primary-600" />,
      value: "98%",
      label: "Satisfaction"
    }
  ];

  const services = [
    {
      icon: <Tool size={24} className="text-primary-600" />,
      title: 'Support Technique',
      description: 'Assistance technique et maintenance',
      link: '/services#maintenance'
    },
    {
      icon: <Shield size={24} className="text-primary-600" />,
      title: 'SAV',
      description: 'Service après-vente et garantie',
      link: '/services'
    },
    {
      icon: <FileText size={24} className="text-primary-600" />,
      title: 'Documentation',
      description: 'Guides et ressources',
      link: '/resources'
    }
  ];

  const selectedOfficeData = offices.find(office => office.id === selectedOffice);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Contact | Proxy Emobility - Contactez Notre Équipe</title>
        <meta name="description" content="Contactez l'équipe Proxy Emobility pour toute question sur nos motos électriques, nos services ou pour devenir partenaire. Nous sommes à votre écoute." />
        <meta name="keywords" content="contact, Proxy Emobility, mobilité électrique, Afrique, Dakar, service client, support technique" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/contact" />
        
        {/* Schema.org structured data for LocalBusiness */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Proxy Emobility",
              "image": "https://proxyemobility.netlify.app/proxy-logo.png",
              "url": "https://proxyemobility.netlify.app/",
              "telephone": "+221 77 123 45 67",
              "email": "hello@proxyemobility.com",
              "address": [
                {
                  "@type": "PostalAddress",
                  "streetAddress": "123 Avenue de l'Indépendance",
                  "addressLocality": "Dakar",
                  "addressRegion": "Dakar",
                  "addressCountry": "Sénégal"
                },
                {
                  "@type": "PostalAddress",
                  "streetAddress": "20 Wenlock Road",
                  "addressLocality": "London",
                  "postalCode": "N1 7GU",
                  "addressCountry": "United Kingdom"
                }
              ],
              "openingHoursSpecification": [
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": [
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday"
                  ],
                  "opens": "09:00",
                  "closes": "18:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  "dayOfWeek": "Saturday",
                  "opens": "09:00",
                  "closes": "13:00"
                }
              ],
              "sameAs": [
                "https://www.facebook.com/proxyemobility",
                "https://www.twitter.com/proxyemobility",
                "https://www.instagram.com/proxyemobility",
                "https://www.linkedin.com/company/proxyemobility"
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Contactez-nous
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Notre équipe est à votre disposition pour répondre à vos questions et vous accompagner dans votre transition vers la mobilité électrique.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ContactForm initialReason={contactReason} />
            <ContactInfo />
          </div>
        </div>
      </section>

      {/* Offices Section */}
      <section id="offices" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Bureaux
            </h2>
            <p className="text-lg text-gray-600">
              Retrouvez-nous à nos bureaux à Dakar et à Londres
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {offices.map((office) => (
              <button
                key={office.id}
                onClick={() => setSelectedOffice(office.id)}
                className={`p-6 rounded-xl transition-all ${
                  selectedOffice === office.id
                    ? 'bg-primary-50 border-2 border-primary-500'
                    : 'bg-white border-2 border-gray-100 hover:border-primary-200'
                }`}
              >
                <div className="text-left">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{office.city}</h3>
                  <p className="text-gray-600 mb-4">{office.country}</p>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin size={16} className="mr-2" />
                      {office.address}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Phone size={16} className="mr-2" />
                      {office.phone}
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock size={16} className="mr-2" />
                      {office.hours}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {selectedOfficeData && (
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {selectedOfficeData.city}, {selectedOfficeData.country}
                  </h3>
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center">
                      <MapPin size={20} className="text-primary-600 mr-3" />
                      <span>{selectedOfficeData.address}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone size={20} className="text-primary-600 mr-3" />
                      <span>{selectedOfficeData.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <Mail size={20} className="text-primary-600 mr-3" />
                      <span>{selectedOfficeData.email}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={20} className="text-primary-600 mr-3" />
                      <span>{selectedOfficeData.hours}</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Notre Équipe</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {selectedOfficeData.team.map((member, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg">
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-600">{member.role}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden">
                  <img
                    src={selectedOfficeData.image}
                    alt={`${selectedOfficeData.city} Office`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Services Client
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez nos services d'assistance et de support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:border-primary-200 transition-all hover:shadow-xl group"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-primary-50 rounded-lg mr-3">
                    {service.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <span className="inline-flex items-center text-primary-600 group-hover:translate-x-2 transition-transform">
                  En savoir plus
                  <ChevronRight size={16} className="ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Questions Fréquentes
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Quels sont vos délais de réponse ?
                </h3>
                <p className="text-gray-600">
                  Nous nous engageons à répondre à toutes les demandes sous 24h ouvrées. Pour les urgences, notre service client est disponible par téléphone.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Comment programmer un essai de moto ?
                </h3>
                <p className="text-gray-600">
                  Vous pouvez programmer un essai directement depuis notre site web ou en contactant notre showroom à Dakar. Un conseiller vous recontactera pour confirmer le rendez-vous.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Proposez-vous un service après-vente ?
                </h3>
                <p className="text-gray-600">
                  Oui, nous disposons d'un réseau de techniciens qualifiés au Sénégal. Notre service après-vente assure maintenance et support technique pour tous nos produits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section id="social" className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Suivez-nous
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Restez connecté avec nous sur les réseaux sociaux
            </p>
            <div className="flex justify-center space-x-6">
              <a
                href="https://www.facebook.com/proxyemobility"
                className="p-4 bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://www.twitter.com/proxyemobility"
                className="p-4 bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <Twitter size={24} />
              </a>
              <a
                href="https://www.instagram.com/proxyemobility"
                className="p-4 bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.linkedin.com/company/proxyemobility"
                className="p-4 bg-primary-50 text-primary-600 rounded-full hover:bg-primary-100 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin size={24} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="py-16 bg-primary-900 text-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">
              Restez Informé
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Inscrivez-vous à notre newsletter pour recevoir nos dernières actualités et offres spéciales.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-sm text-white placeholder-white/60 rounded-xl border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
                aria-label="Adresse email"
              />
              <button
                type="submit"
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center justify-center"
                aria-label="S'abonner à la newsletter"
              >
                S'abonner
                <Send size={20} className="ml-2" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;